import React from 'react';
import { NavLink } from 'react-router-dom';
import parse, { attributesToProps, domToReact } from 'html-react-parser';
import '../pages/SitePage.css';
import BadWords from '../util/badWords';
import { convertUrls } from '../util/util';
import ViewImageModal from '../modals/ViewImageModal';

interface ContentDivProps {
  content: string;
  onImageLoad?: Function;
  summary?: boolean;
}

interface ContentDivState {
  viewImage: string;
}

class ContentDiv extends React.Component<ContentDivProps, ContentDivState> {
  parseOptions = {
    replace: (domNode: any) => {
      if (domNode.attribs && domNode.name === 'img') {
        const props = attributesToProps(domNode.attribs);
        if(props.src.indexOf('https://s3.us-west-2.amazonaws.com/oneclick.media') != -1)
          props.src = props.src.replace('https://s3.us-west-2.amazonaws.com/oneclick.media', 'https://d27jhqexqrz634.cloudfront.net')
        return (
          <div className="ql-editor-image-container">
            <img 
              className='ql-editor-image' 
              onClick={(e)=>this.onViewImage(e, props.src)} 
              onLoad={(e)=>{if(this.props.onImageLoad) this.props.onImageLoad(props.src)}}
              {...props} 
            />
          </div>
        );
      }
  
      if (domNode.attribs && domNode.name === 'slug') {
        const props = attributesToProps(domNode.attribs);
        return <NavLink style={{textDecoration: 'none'}} onClick={(e)=>e.stopPropagation()} to={props.href}>{props.id}</NavLink>;
      }

      if (domNode.attribs && domNode.name === 'hashtag') {
        const props = attributesToProps(domNode.attribs);
        return <NavLink style={{textDecoration: 'none'}} onClick={(e)=>this.onHashtag(e)} to={props.href} >{props.id}</NavLink>;
      }

      if (domNode.attribs && domNode.name === 'a') {
        const props = attributesToProps(domNode.attribs);
        const regex = /(https?:\/\/.*\.(?:png|jpg|jpeg|gif))/i;

        if (regex.test(props.href)) {
          return (
            <div>
              <a {...props}>{props.href}</a>
              <img 
                style={{margin: '8px 0'}} 
                className='ql-editor-image' 
                src={props.href} 
                onClick={(e)=>this.onViewImage(e, props.href)} 
              />
            </div>
          )
        }

        if(props.href.indexOf(window.location.host) != -1) {
          return <NavLink className={props.className} onClick={(e)=>e.stopPropagation()} to={props.href}>{domToReact(domNode.children)}</NavLink>;
        }
        else {
          return <a {...props} onClick={(e)=>e.stopPropagation()}>{domToReact(domNode.children)}</a>;
        }
      }

      if(domNode.name == 'iframe' && domNode.attribs && domNode.attribs.src.includes('www.youtube.com')) {
        domNode.attribs.src = domNode.attribs.src.replace('www.youtube.com', 'www.youtube-nocookie.com');
        return domNode;
      }
    }
  };

  constructor(props: ContentDivProps) {
    super(props);

    this.state = {
      viewImage: ''
    };
  }
  
  onViewImage(e:any, src:string) {
    e.stopPropagation();
    this.setState({viewImage: src})
  }

  onHashtag(e:any) {
    e.stopPropagation();
    // if (this.props.onHashtag)
    //   this.props.onHashtag();
  }

  truncateHtmlByVisibleText(html: string, maxVisibleChars: number): string {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    
    const countVisibleText = (node: Node): number => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent || '';
        return text.length;
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        let count = 0;
        for (let i = 0; i < node.childNodes.length; i++) {
          count += countVisibleText(node.childNodes[i]);
        }
        return count;
      }
      return 0;
    };
    
    const countMediaElements = (node: Node): number => {
      if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as Element;
        let count = 0;
        
        const tagName = element.tagName.toLowerCase();
        if (tagName === 'img' || tagName === 'audio' || tagName === 'video') {
          count = 1;
        }
        
        for (let i = 0; i < node.childNodes.length; i++) {
          count += countMediaElements(node.childNodes[i]);
        }
        
        return count;
      }
      return 0;
    };
    
    const totalVisibleChars = countVisibleText(doc.body);
    const totalMediaElements = countMediaElements(doc.body);
    
    if (totalVisibleChars <= maxVisibleChars && totalMediaElements <= 3) {
      return html;
    }
    
    let accumulatedChars = 0;
    let accumulatedMedia = 0;
    let truncateAtElementIndex = -1;
    
    for (let i = 0; i < doc.body.childNodes.length; i++) {
      const child = doc.body.childNodes[i];
      const childCharCount = countVisibleText(child);
      const childMediaCount = countMediaElements(child);
      
      if (accumulatedMedia + childMediaCount > 3) {
        truncateAtElementIndex = i;
        break;
      }
      
      if (accumulatedChars + childCharCount > maxVisibleChars) {
        truncateAtElementIndex = i;
        break;
      }
      
      accumulatedChars += childCharCount;
      accumulatedMedia += childMediaCount;
      
      if (accumulatedMedia >= 3) {
        truncateAtElementIndex = i + 1;
        break;
      }
    }
    
    if (truncateAtElementIndex === -1 || truncateAtElementIndex === 0) {
      return html;
    }
    
    let depth = 0;
    let topLevelElementIndex = 0;
    let truncatePosition = -1;
    let i = 0;
    
    while (i < html.length) {
      if (html[i] === '<') {
        const tagEnd = html.indexOf('>', i);
        if (tagEnd === -1) break;
        
        const tagContent = html.substring(i + 1, tagEnd);
        const isClosingTag = tagContent.startsWith('/');
        const isSelfClosing = tagContent.endsWith('/') || ['br', 'hr', 'img', 'input', 'meta', 'link'].some(tag => 
          tagContent.toLowerCase().startsWith(tag + ' ') || tagContent.toLowerCase() === tag
        );
        
        if (isClosingTag) {
          depth--;
        } else if (!isSelfClosing) {
          if (depth === 0) {
            if (topLevelElementIndex === truncateAtElementIndex) {
              truncatePosition = i;
              break;
            }
            topLevelElementIndex++;
          }
          depth++;
        } else if (depth === 0) {
          if (topLevelElementIndex === truncateAtElementIndex) {
            truncatePosition = i;
            break;
          }
          topLevelElementIndex++;
        }
        
        i = tagEnd + 1;
      } else if (depth === 0 && html[i].trim().length > 0) {
        let textStart = i;
        while (i < html.length && html[i] !== '<') {
          i++;
        }
        const text = html.substring(textStart, i).trim();
        if (text.length > 0) {
          if (topLevelElementIndex === truncateAtElementIndex) {
            truncatePosition = textStart;
            break;
          }
          topLevelElementIndex++;
        }
      } else {
        i++;
      }
    }
    
    if (truncatePosition === -1 || truncatePosition >= html.length) {
      return html;
    }
    
    const truncatedHtml = html.substring(0, truncatePosition);
    
    return truncatedHtml + '<p><br>(more...)</p>';
  }

  render() {
    let content = decodeURIComponent(this.props.content);
    content     = BadWords.clean(content);
    content     = convertUrls(content);

    if(this.props.summary) {
      content = this.truncateHtmlByVisibleText(content, 1000);
    }

    return (
      <div>
        <div style={{wordWrap: 'break-word'}}>
          {parse(content, this.parseOptions)}
        </div>
        {this.state.viewImage &&
          <ViewImageModal 
            src={this.state.viewImage} 
            onClose={()=>this.setState({viewImage: ''})} 
          />
        }
      </div>
    )
  }
}

export default ContentDiv;